import { ApolloError } from "@apollo/client";

export interface ErrorResponse {
  message: string;
  code?: string;
  details?: any;
}

export class ApolloErrorHandler {
  static handle(error: ApolloError): ErrorResponse {
    // Network errors
    if (error.networkError) {
      return {
        message:
          "Network connection error. Please check your internet connection.",
        code: "NETWORK_ERROR",
      };
    }

    // GraphQL errors
    if (error.graphQLErrors?.length > 0) {
      const graphQLError = error.graphQLErrors[0];

      // Handle specific error codes
      switch (graphQLError.extensions?.code) {
        case "UNAUTHENTICATED":
          return {
            message: "Your session has expired. Please login again.",
            code: "UNAUTHENTICATED",
          };
        case "FORBIDDEN":
          return {
            message: "You don't have permission to perform this action.",
            code: "FORBIDDEN",
          };
        case "VALIDATION_ERROR":
          return {
            message: this.formatValidationError(graphQLError),
            code: "VALIDATION_ERROR",
            details: graphQLError.extensions?.validationErrors,
          };
        case "UNIQUE_CONSTRAINT_ERROR":
          return {
            message: "This record already exists.",
            code: "UNIQUE_CONSTRAINT_ERROR",
          };
        default:
          return {
            message: graphQLError.message || "An unexpected error occurred.",
            code: (graphQLError.extensions?.code as string) || "UNKNOWN_ERROR",
          };
      }
    }

    // Handle specific error messages
    if (error.message.includes("email already exists")) {
      return {
        message: "This email is already registered. Please login instead.",
        code: "EMAIL_EXISTS",
      };
    }

    // Default error
    return {
      message: "An unexpected error occurred. Please try again later.",
      code: "UNKNOWN_ERROR",
    };
  }

  private static formatValidationError(error: any): string {
    const validationErrors = error.extensions?.validationErrors;
    if (!validationErrors) return "Validation error occurred.";

    const messages = Object.values(validationErrors)
      .flat()
      .map((err: any) => err.message)
      .join(", ");

    return messages || "Please check your input and try again.";
  }

  // Development logging
  static logError(error: ApolloError): void {
    if (process.env.NODE_ENV === "development") {
      console.error("Apollo Error:", {
        message: error.message,
        networkError: error.networkError,
        graphQLErrors: error.graphQLErrors,
      });
    }
  }
}
